import { get, onValue, push, remove, set } from 'firebase/database';
import { tenantRef, logPosEvent } from './posUtil';
import { userFacingError } from './imageCompressionUtil';
import {
  POS_CUSTOM_FONTS_NODE,
  POS_CUSTOM_FONT_DATA_NODE,
  POS_CUSTOM_FONT_PREFIX,
  POS_CUSTOM_FONT_FAMILY_PREFIX,
  POS_CUSTOM_FONT_TYPES,
  POS_CUSTOM_FONT_EXTENSIONS,
  POS_CUSTOM_FONT_MAX_BYTES,
  POS_CUSTOM_FONT_NAME_MAX_LENGTH,
  POS_CUSTOM_FONTS_MAX,
  POS_AUDIT_ACTIONS,
} from '../constants/posConst';

// ---- Uploaded fonts (.ttf, .otf, .woff, .woff2) ---------------------------
//
// A club's own typeface, uploaded from Config -> Store Details. The file is
// stored as a data URL in the database (this deployment has no Storage bucket
// in use), in its own node so that only a till actually USING the font pays to
// download it. The branding node then stores "custom-<id>" as its font choice,
// exactly like one of the built-in keys.

/** Font ids already registered with document.fonts, by client + id. */
const loaded = new Map();

/**
 * Whether a stored font choice points at an uploaded font.
 * @param {string} [key]
 * @returns {boolean}
 */
export const isPosCustomFontKey = (key) => typeof key === 'string' && key.startsWith(POS_CUSTOM_FONT_PREFIX);

/**
 * The font choice key for an uploaded font id.
 * @param {string} id
 * @returns {string} e.g. "custom-OaB3…"
 */
export const posCustomFontKey = (id) => `${POS_CUSTOM_FONT_PREFIX}${id}`;

/**
 * The uploaded font id inside a font choice key.
 * @param {string} key
 * @returns {string} '' when the key is not a custom one
 */
export const posCustomFontId = (key) => (isPosCustomFontKey(key) ? key.slice(POS_CUSTOM_FONT_PREFIX.length) : '');

/**
 * The CSS family name an uploaded font is registered under. Derived from the
 * id rather than the display name, so a name with quotes or odd characters can
 * never reach a stylesheet.
 *
 * @param {string} id
 * @returns {string}
 */
export const posCustomFontFamily = (id) => `${POS_CUSTOM_FONT_FAMILY_PREFIX}${String(id).replace(/[^A-Za-z0-9_-]/g, '')}`;

/**
 * Live list of this client's uploaded fonts (names only, never the files).
 *
 * @param {string} clientId
 * @param {(fonts: Array<{id: string, name: string, fileName: string, size: number, createdAt: number}>) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribePosCustomFonts = (clientId, callback) => onValue(
  tenantRef(clientId, POS_CUSTOM_FONTS_NODE),
  (snapshot) => {
    const out = [];
    snapshot.forEach((child) => {
      const value = child.val() || {};
      out.push({
        id: child.key,
        name: String(value.name || 'Uploaded font'),
        fileName: String(value.fileName || ''),
        size: Number(value.size) || 0,
        createdAt: Number(value.createdAt) || 0,
      });
    });
    out.sort((a, b) => a.name.localeCompare(b.name));
    callback(out);
  },
  (error) => {
    console.error('POS custom fonts listener failed:', error);
    callback([]);
  },
);

/**
 * Reads a file as a base64 data URL.
 * @param {File} file
 * @returns {Promise<string>}
 */
const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(userFacingError('That file could not be read. Try choosing it again.'));
  reader.readAsDataURL(file);
});

/**
 * The accepted font type a file name ends in, or null.
 *
 * @param {string} fileName
 * @returns {object|null} a POS_CUSTOM_FONT_TYPES entry
 */
const fontTypeOf = (fileName) => {
  const lower = String(fileName || '').toLowerCase();
  // Longest extension first, so ".woff2" is not taken for ".woff".
  return [...POS_CUSTOM_FONT_TYPES]
    .sort((a, b) => b.ext.length - a.ext.length)
    .find((type) => lower.endsWith(type.ext)) || null;
};

/**
 * The accepted font type a stored data URL holds, or null.
 *
 * @param {string} dataUrl
 * @returns {object|null} a POS_CUSTOM_FONT_TYPES entry
 */
const fontTypeOfDataUrl = (dataUrl) => POS_CUSTOM_FONT_TYPES
  .find((type) => String(dataUrl || '').startsWith(`data:${type.mime};base64,`)) || null;

/**
 * Checks that a picked file is a usable font before anything is stored: the
 * extension, the size, the file's own signature, and that the browser can
 * actually load it. A file that fails here would otherwise be saved and then
 * silently fall back to another typeface on every till.
 *
 * @param {File} file
 * @returns {Promise<object>} the file's POS_CUSTOM_FONT_TYPES entry
 * @throws {Error} user-facing, naming what is wrong
 */
const validateFontFile = async (file) => {
  const accepted = POS_CUSTOM_FONT_EXTENSIONS.join(', ');
  if (!file) throw userFacingError(`Choose a font file first (${accepted}).`);
  const type = fontTypeOf(file.name);
  if (!type) throw userFacingError(`Only ${accepted} font files can be imported.`);
  if (file.size > POS_CUSTOM_FONT_MAX_BYTES) {
    throw userFacingError(`That font is ${(file.size / 1024 / 1024).toFixed(1)}MB. Fonts can be at most ${(POS_CUSTOM_FONT_MAX_BYTES / 1024 / 1024).toFixed(1)}MB.`);
  }

  const buffer = await file.arrayBuffer();
  const signature = buffer.byteLength >= 4 ? new DataView(buffer).getUint32(0) : 0;
  if (!type.signatures.includes(signature)) {
    throw userFacingError(`That file is not a ${type.label} font, even though it ends in ${type.ext}.`);
  }

  if (typeof FontFace !== 'undefined') {
    try {
      await new FontFace('PosFontCheck', buffer).load();
    } catch {
      throw userFacingError('This browser could not load that font file. It may be damaged.');
    }
  }
  return type;
};

/**
 * Uploads one font file (.ttf, .otf, .woff or .woff2) for this client.
 *
 * The file data is written first and the list entry second, so a font can
 * never appear in the dropdown without its file behind it.
 *
 * @param {string} clientId
 * @param {File} file - the font file
 * @param {number} existingCount - fonts already uploaded, for the cap
 * @param {string} [actor] - the staff address to record
 * @returns {Promise<{id: string, key: string, name: string}>}
 * @throws {Error} user-facing on a bad file or a full list
 */
export const uploadPosCustomFont = async (clientId, file, existingCount, actor = '') => {
  if (existingCount >= POS_CUSTOM_FONTS_MAX) {
    throw userFacingError(`You can keep up to ${POS_CUSTOM_FONTS_MAX} uploaded fonts. Remove one first.`);
  }
  const type = await validateFontFile(file);

  const raw = await readAsDataUrl(file);
  const base64 = raw.slice(raw.indexOf(',') + 1);
  if (!base64) throw userFacingError('That file appears to be empty.');
  // Normalised: browsers report fonts as font/ttf, application/x-font-ttf,
  // application/octet-stream or nothing at all, and the rules expect font/*.
  // nothing at all, and the rules only accept one prefix.
  const dataUrl = `data:${type.mime};base64,${base64}`;

  const name = String(file.name).slice(0, -type.ext.length).trim().slice(0, POS_CUSTOM_FONT_NAME_MAX_LENGTH) || 'Uploaded font';
  const entryRef = push(tenantRef(clientId, POS_CUSTOM_FONTS_NODE));
  const id = entryRef.key;

  await set(tenantRef(clientId, `${POS_CUSTOM_FONT_DATA_NODE}/${id}`), dataUrl);
  await set(entryRef, {
    name,
    fileName: String(file.name).slice(0, 120),
    size: file.size,
    createdAt: Date.now(),
  });

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.FONT_UPLOADED,
    entityId: id,
    summary: { name, fileName: file.name, sizeKB: Math.round(file.size / 1024) },
  });
  return { id, key: posCustomFontKey(id), name };
};

/**
 * Removes one uploaded font — its list entry first, so it leaves the dropdown
 * before its file is gone.
 *
 * @param {string} clientId
 * @param {{id: string, name: string}} font
 * @param {string} [actor]
 * @returns {Promise<void>}
 */
export const removePosCustomFont = async (clientId, font, actor = '') => {
  await remove(tenantRef(clientId, `${POS_CUSTOM_FONTS_NODE}/${font.id}`));
  await remove(tenantRef(clientId, `${POS_CUSTOM_FONT_DATA_NODE}/${font.id}`));
  loaded.delete(`${clientId}/${font.id}`);

  logPosEvent({
    clientId,
    actor,
    action: POS_AUDIT_ACTIONS.FONT_REMOVED,
    entityId: font.id,
    summary: { name: font.name },
  });
};

/**
 * Downloads an uploaded font once and registers it with the document, so any
 * stylesheet naming posCustomFontFamily(id) starts drawing in it.
 *
 * Cached per client + id: the Store Details preview and the app-wide theme
 * both ask for it, and the file is only fetched once.
 *
 * @param {string} clientId
 * @param {string} id - the uploaded font's id
 * @returns {Promise<string>} the CSS family name
 * @throws {Error} when the file is missing or cannot be loaded
 */
export const loadPosCustomFont = (clientId, id) => {
  const cacheKey = `${clientId}/${id}`;
  if (loaded.has(cacheKey)) return loaded.get(cacheKey);

  const task = (async () => {
    const snapshot = await get(tenantRef(clientId, `${POS_CUSTOM_FONT_DATA_NODE}/${id}`));
    const dataUrl = snapshot.val();
    const type = fontTypeOfDataUrl(dataUrl);
    if (!type) throw new Error(`Uploaded font ${id} has no usable file.`);
    const family = posCustomFontFamily(id);
    const face = new FontFace(family, `url(${dataUrl}) format('${type.format}')`);
    await face.load();
    document.fonts.add(face);
    return family;
  })();

  // A failure is not cached, so the next attempt can try again.
  task.catch(() => loaded.delete(cacheKey));
  loaded.set(cacheKey, task);
  return task;
};
